import type { ReactNode } from 'react';
import { HelpTip } from '../HelpTip';
import { useDeferredError } from './useDeferredError';

export interface RadioOption<T extends string> {
  value: T;
  label: string;
  /** Second line under the label. Explains what choosing this actually does. */
  description?: string;
  disabled?: boolean;
  /**
   * Facts shown at the card's RIGHT edge — what choosing this option would do to a figure
   * (Leave History's balance and remaining balance). Read-only: it sits inside the `<label>`,
   * so it must hold no control of its own.
   */
  aside?: ReactNode;
}

/**
 * A small set of mutually exclusive choices, rendered as cards — the shared control for
 * picking a mode inside a form. Never a native `<select>`, and never a checkbox pair.
 *
 * Real `<input type="radio">` elements share one `name`, so arrow-key roving, form
 * semantics, and screen-reader grouping all come from the platform rather than from ARIA
 * bolted on afterwards. Label/description/error wiring matches `<TextField>`.
 */
export function RadioGroup<T extends string>({
  value,
  onChange,
  options,
  label,
  description,
  error,
  required = false,
  disabled = false,
  columns = 2,
  id,
}: {
  value: T;
  onChange: (next: T) => void;
  options: RadioOption<T>[];
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  columns?: 1 | 2;
  id: string;
}) {
  /*
    A required-but-empty field must not open red. `useDeferredError` holds back an error the
    control has carried since it mounted until the reader has touched it, and shows anything
    that arrives later — a server rejection included — immediately.
  */
  const { error: shownError, markTouched } = useDeferredError(error);

  const describedBy = [description ? `${id}-desc` : null, shownError ? `${id}-err` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <fieldset
      className="flex min-w-0 flex-col gap-1.5"
      aria-describedby={describedBy || undefined}
      // Leaving the group counts as having answered it, exactly as blurring a text field does.
      onBlur={markTouched}
    >
      {label ? (
        <div className="flex min-h-5 items-center gap-1.5">
          <legend className="text-sm text-slate-700">
            {label}
            {required ? (
              <span className="ml-0.5 text-red-600" aria-hidden="true">
                *
              </span>
            ) : null}
          </legend>

          {description ? <HelpTip label={description} /> : null}
        </div>
      ) : null}

      {description ? (
        <span id={`${id}-desc`} className="sr-only">
          {description}
        </span>
      ) : null}

      <div className={`grid gap-2 ${columns === 2 ? 'sm:grid-cols-2' : ''}`}>
        {options.map((option) => {
          const selected = option.value === value;
          const optionDisabled = disabled || option.disabled;

          return (
            <label
              key={option.value}
              htmlFor={`${id}-${option.value}`}
              className={[
                'flex items-start gap-2.5 rounded-lg border p-3 transition',
                optionDisabled ? 'cursor-not-allowed bg-slate-50' : 'cursor-pointer',
                selected && !optionDisabled
                  ? 'border-blue-500 bg-blue-50/50'
                  : 'border-slate-300 hover:border-slate-400',
              ].join(' ')}
            >
              <input
                id={`${id}-${option.value}`}
                name={id}
                type="radio"
                value={option.value}
                checked={selected}
                onChange={() => {
                  markTouched();
                  onChange(option.value);
                }}
                disabled={optionDisabled}
                className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-blue-500 focus:ring-2 focus:ring-blue-200"
              />

              <span className="min-w-0 flex-1">
                <span className="block text-sm text-slate-700">{option.label}</span>
                {option.description ? (
                  <span className="mt-0.5 block text-xs text-slate-500">{option.description}</span>
                ) : null}
              </span>

              {option.aside != null ? <span className="shrink-0 self-center">{option.aside}</span> : null}
            </label>
          );
        })}
      </div>

      {shownError ? (
        <p id={`${id}-err`} className="text-xs text-red-600" role="alert">
          {shownError}
        </p>
      ) : null}
    </fieldset>
  );
}
